import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';
import React, { useState, useEffect } from 'react';
import {
  Container, Box, Typography, Grid, Card, CardContent, Chip, Button,
  Skeleton, Alert, Link as MuiLink, Fab, useMediaQuery, useTheme,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Schedule as DurationIcon,
  AttachMoney as BudgetIcon,
  CalendarMonth as CalendarIcon,
  Hotel as StayIcon,
  Flight as FlightIcon,
  Restaurant as EatIcon,
  LocalBar as HangoutIcon,
  Attractions as ActivityIcon,
  ConfirmationNumber as TicketIcon,
  Lightbulb as TipIcon,
  ArrowBack as BackIcon,
  FlightTakeoff as TripIcon,
  OpenInNew as ExternalIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import api from '../config/api';
import { THEME_COLORS } from '../constants/theme';
import CreateTripFromEventDialog from '../components/CreateTripFromEventDialog';

const categoryConfig = {
  sport:           { label: 'Sport',      color: '#e53935', emoji: '🏆' },
  music:           { label: 'Music',      color: '#8e24aa', emoji: '🎵' },
  cultural:        { label: 'Cultural',   color: '#6d4c41', emoji: '🎭' },
  food:            { label: 'Food',       color: '#ef6c00', emoji: '🍴' },
  theme_park:      { label: 'Theme Park', color: '#1e88e5', emoji: '🎢' },
  city_experience: { label: 'City',       color: '#00897b', emoji: '🏙️' },
  layover:         { label: 'Layover',    color: '#546e7a', emoji: '✈️' },
  nature:          { label: 'Nature',     color: '#43a047', emoji: '🌿' },
  festival:        { label: 'Festival',   color: '#d81b60', emoji: '🎉' },
  expo:            { label: 'Expo',       color: '#5c6bc0', emoji: '🎪' },
  parade:          { label: 'Parade',     color: '#f4511e', emoji: '🎊' },
  seasonal:        { label: 'Seasonal',   color: '#26a69a', emoji: '🌸' },
  cruise:          { label: 'Cruise',    color: '#0277bd', emoji: '🚢' },
};

const priorityColors = {
  'must-do': { bg: '#e53935', text: 'Must Do' },
  'recommended': { bg: '#1e88e5', text: 'Recommended' },
  'optional': { bg: '#757575', text: 'Optional' },
};

const tipCategoryIcons = {
  transport: '🚇', packing: '🎒', money: '💰', culture: '🎭',
  food: '🍴', safety: '🛡️', timing: '⏰', group: '👥',
};

const EventGuideDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tripDialogOpen, setTripDialogOpen] = useState(false);

  useEffect(() => {
    const fetchGuide = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/event-guides/${slug}`);
        setGuide(response.data.guide);
      } catch (err) {
        console.error('Error fetching event guide:', err);
        setError(err.response?.status === 404 ? 'Event guide not found.' : 'Failed to load event guide.');
      } finally {
        setLoading(false);
      }
    };
    fetchGuide();
  }, [slug]);

  if (loading) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
        <Skeleton variant="rounded" height={350} sx={{ borderRadius: 3, mb: 3 }} />
        <Skeleton width="60%" height={40} sx={{ mb: 1 }} />
        <Skeleton width="40%" height={24} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 6, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/event-guides')} sx={{ textTransform: 'none' }}>
          Back to Event Guides
        </Button>
      </Container>
    );
  }

  if (!guide) return null;

  const catCfg = categoryConfig[guide.category] || { label: guide.category, color: '#607d8b', emoji: '📌' };
  const stay = guide.section_stay || [];
  const flights = guide.section_flights || {};
  const eat = guide.section_eat || [];
  const hangouts = guide.section_hangouts || [];
  const activities = guide.section_activities || [];
  const tickets = guide.section_tickets || {};
  const tips = guide.section_tips || [];

  // Build rich structured data
  const structuredData = [];

  // Event schema for events with dates
  if (guide.next_event_date) {
    structuredData.push({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: guide.title,
      description: guide.subtitle || guide.description?.substring(0, 300),
      startDate: guide.next_event_date,
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      location: {
        '@type': 'Place',
        name: guide.location_name,
        address: {
          '@type': 'PostalAddress',
          addressCountry: guide.location_country,
        },
        ...(guide.latitude && guide.longitude ? {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: parseFloat(guide.latitude),
            longitude: parseFloat(guide.longitude),
          },
        } : {}),
      },
      image: guide.cover_image || undefined,
      ...(guide.budget_estimate_low ? {
        offers: {
          '@type': 'Offer',
          price: parseFloat(guide.budget_estimate_low),
          priceCurrency: guide.budget_currency || 'USD',
          availability: 'https://schema.org/InStock',
        },
      } : {}),
      organizer: {
        '@type': 'Organization',
        name: 'Déjà Vu Nomad',
        url: 'https://dejavunomad.com',
      },
    });
  }

  // Article schema for the guide content
  structuredData.push({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${guide.title} Travel Guide`,
    description: guide.subtitle || guide.description?.substring(0, 300),
    image: guide.cover_image || undefined,
    author: {
      '@type': 'Organization',
      name: 'Déjà Vu Nomad',
      url: 'https://dejavunomad.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Déjà Vu Nomad',
      url: 'https://dejavunomad.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://dejavunomad.com/noBgColor.png',
      },
    },
    datePublished: guide.created_at,
    dateModified: guide.updated_at || guide.created_at,
    mainEntityOfPage: `https://dejavunomad.com/event-guides/${guide.slug}`,
  });

  // FAQPage schema from tips
  if (tips.length > 0) {
    structuredData.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: tips.map(tip => ({
        '@type': 'Question',
        name: `What's a ${tip.category} tip for ${guide.title}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: tip.tip,
        },
      })),
    });
  }

  // Build SEO description
  const seoDescription = guide.subtitle
    || `Complete travel guide for ${guide.title} in ${guide.location_name}. Where to stay, eat, things to do, tickets, and insider tips for ${guide.recommended_days} days.`;

  return (
    <>
      <SEOHead
        title={`${guide.title} Travel Guide - ${guide.location_name}`}
        description={seoDescription}
        canonical={`/event-guides/${guide.slug}`}
        ogImage={guide.cover_image}
        ogType="article"
        keywords={`${guide.title}, ${guide.location_name}, travel guide, ${categoryConfig[guide.category]?.label || ''}, where to stay, things to do, ${(guide.tags || []).join(', ')}`}
        structuredData={structuredData}
      />

      {/* Back Navigation Bar */}
      <Box sx={{ bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', py: 1.5 }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/event-guides')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.primary',
              px: 1.5,
              borderRadius: 2,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            Back to Event Guides
          </Button>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{
        position: 'relative',
        height: { xs: 280, md: 420 },
        overflow: 'hidden',
      }}>
        <Box
          component="img"
          src={guide.cover_image}
          alt={guide.title}
          onError={(e) => { e.target.onerror = null; e.target.src = `https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80`; }}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.1) 100%)',
        }} />
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          pb: { xs: 3, md: 4 }, color: 'white',
        }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label={`${catCfg.emoji} ${catCfg.label}`}
              size="small"
              sx={{ bgcolor: catCfg.color, color: 'white', fontWeight: 700 }}
            />
            {guide.typical_dates && (
              <Chip
                icon={<CalendarIcon sx={{ fontSize: 14, color: 'white !important' }} />}
                label={guide.typical_dates}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            )}
          </Box>
          <Typography variant={isMobile ? 'h4' : 'h2'} sx={{ fontWeight: 800, lineHeight: 1.1, mb: 1 }}>
            {guide.title}
          </Typography>
          {guide.subtitle && (
            <Typography variant={isMobile ? 'body2' : 'h6'} sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 700 }}>
              {guide.subtitle}
            </Typography>
          )}
        </Container>
      </Box>

      {/* Quick Facts Bar */}
      <Box sx={{ bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', py: 2 }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationIcon sx={{ fontSize: 18, color: THEME_COLORS.primary.main }} />
              <Typography variant="body2" fontWeight={600}>{guide.location_name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DurationIcon sx={{ fontSize: 18, color: THEME_COLORS.primary.main }} />
              <Typography variant="body2" fontWeight={600}>{guide.recommended_days} days recommended</Typography>
            </Box>
            {(guide.budget_estimate_low || guide.budget_estimate_high) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BudgetIcon sx={{ fontSize: 18, color: THEME_COLORS.primary.main }} />
                <Typography variant="body2" fontWeight={600}>
                  {guide.budget_currency} {guide.budget_estimate_low ? Math.round(guide.budget_estimate_low).toLocaleString() : '?'}
                  {' – '}
                  {guide.budget_estimate_high ? Math.round(guide.budget_estimate_high).toLocaleString() : '?'}
                </Typography>
              </Box>
            )}
            {guide.typical_dates && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 18, color: THEME_COLORS.primary.main }} />
                <Typography variant="body2" fontWeight={600}>{guide.typical_dates}</Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* Content Sections */}
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
        {/* Description */}
        {guide.description && (
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, color: 'text.secondary', maxWidth: 800 }}>
            {guide.description}
          </Typography>
        )}

        <Grid container spacing={4}>
          {/* LEFT COLUMN - Main content */}
          <Grid item xs={12} md={8}>
            {/* WHERE TO STAY */}
            {stay.length > 0 && (
              <SectionCard icon={<StayIcon />} title="Where to Stay" color="#1e88e5">
                <Grid container spacing={2}>
                  {stay.map((s, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight={700}>{s.name}</Typography>
                            {s.priceRange && <Chip label={s.priceRange} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22 }} />}
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>{s.description}</Typography>
                          {s.area && <Chip label={s.area} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20, mr: 0.5 }} />}
                          {s.tip && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#1e88e5', fontWeight: 500 }}>
                              💡 {s.tip}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </SectionCard>
            )}

            {/* GETTING THERE */}
            {(flights.airports?.length > 0 || flights.tips?.length > 0) && (
              <SectionCard icon={<FlightIcon />} title="Getting There" color="#00897b">
                {flights.airports?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Airports</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {flights.airports.map((a, i) => (
                        <Chip key={i} label={a} variant="outlined" size="small" icon={<FlightIcon sx={{ fontSize: 14 }} />} sx={{ fontWeight: 500 }} />
                      ))}
                    </Box>
                  </Box>
                )}
                {flights.bestBookingWindow && (
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    <strong>Best booking window:</strong> {flights.bestBookingWindow}
                  </Typography>
                )}
                {flights.tips?.length > 0 && (
                  <Box>
                    {flights.tips.map((tip, i) => (
                      <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                        ✈️ {tip}
                      </Typography>
                    ))}
                  </Box>
                )}
              </SectionCard>
            )}

            {/* WHERE TO EAT */}
            {eat.length > 0 && (
              <SectionCard icon={<EatIcon />} title="Where to Eat" color="#ef6c00">
                <Grid container spacing={2}>
                  {eat.map((r, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight={700}>{r.name}</Typography>
                            {r.priceRange && <Chip label={r.priceRange} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22 }} />}
                          </Box>
                          {r.cuisine && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{r.cuisine}</Typography>
                          )}
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 0.5, fontSize: '0.8rem' }}>{r.description}</Typography>
                          {r.mustTry && (
                            <Typography variant="caption" sx={{ color: '#ef6c00', fontWeight: 600 }}>
                              Must try: {r.mustTry}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </SectionCard>
            )}

            {/* POPULAR HANGOUT SPOTS */}
            {hangouts.length > 0 && (
              <SectionCard icon={<HangoutIcon />} title="Popular Hangout Spots" color="#8e24aa">
                <Grid container spacing={2}>
                  {hangouts.map((h, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>{h.name}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>{h.description}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {h.vibe && <Chip label={h.vibe} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />}
                            {h.bestTime && <Chip label={`Best: ${h.bestTime}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </SectionCard>
            )}

            {/* THINGS TO DO */}
            {activities.length > 0 && (
              <SectionCard icon={<ActivityIcon />} title="Things to Do" color="#e53935">
                {activities.map((a, i) => {
                  const prio = priorityColors[a.priority] || null;
                  return (
                    <Card key={i} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={700}>{a.name}</Typography>
                          {prio && (
                            <Chip label={prio.text} size="small" sx={{ bgcolor: prio.bg, color: 'white', fontWeight: 700, fontSize: '0.65rem', height: 22 }} />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>{a.description}</Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          {a.duration && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <DurationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">{a.duration}</Typography>
                            </Box>
                          )}
                          {a.cost && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <BudgetIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">{a.cost}</Typography>
                            </Box>
                          )}
                        </Box>
                        {a.tip && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#1e88e5', fontWeight: 500 }}>
                            💡 {a.tip}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </SectionCard>
            )}
          </Grid>

          {/* RIGHT COLUMN - Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Plan This Trip CTA */}
            <Card sx={{
              borderRadius: 3,
              mb: 3,
              border: '2px solid',
              borderColor: THEME_COLORS.primary.main,
              position: { md: 'sticky' },
              top: { md: 80 },
            }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <TripIcon sx={{ fontSize: 40, color: THEME_COLORS.primary.main, mb: 1 }} />
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Ready to go?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create a trip pre-loaded with activities from this guide.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<TripIcon />}
                  onClick={() => setTripDialogOpen(true)}
                  sx={{
                    textTransform: 'none', fontWeight: 700, borderRadius: 2,
                    bgcolor: THEME_COLORS.primary.main,
                    '&:hover': { bgcolor: THEME_COLORS.primary.dark || '#3d0f0f' },
                  }}
                >
                  Plan This Trip
                </Button>
              </CardContent>
            </Card>

            {/* TICKETS & ACCESS */}
            {(tickets.overview || tickets.sources?.length > 0) && (
              <SectionCard icon={<TicketIcon />} title="Tickets & Access" color="#f59e0b" sx={{ mb: 3 }}>
                {tickets.overview && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
                    {tickets.overview}
                  </Typography>
                )}
                {tickets.avgPrice && (
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
                    Average price: {tickets.avgPrice}
                  </Typography>
                )}
                {tickets.sources?.map((src, i) => (
                  <Card key={i} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" fontWeight={600}>{src.name}</Typography>
                        {src.url && (
                          <MuiLink href={src.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center' }}>
                            <ExternalIcon sx={{ fontSize: 16 }} />
                          </MuiLink>
                        )}
                      </Box>
                      {src.tip && (
                        <Typography variant="caption" color="text.secondary">{src.tip}</Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </SectionCard>
            )}

            {/* TRAVEL TIPS */}
            {tips.length > 0 && (
              <SectionCard icon={<TipIcon />} title="Travel Tips" color="#43a047">
                {tips.map((tip, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
                    <Typography sx={{ fontSize: '1.2rem', lineHeight: 1.4 }}>
                      {tipCategoryIcons[tip.category] || '💡'}
                    </Typography>
                    <Box>
                      {tip.category && (
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>
                          {tip.category}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{tip.tip}</Typography>
                    </Box>
                  </Box>
                ))}
              </SectionCard>
            )}

            {/* Tags */}
            {guide.tags?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>Tags</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {guide.tags.map((tag, i) => (
                    <Chip key={i} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                  ))}
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Floating CTA for mobile */}
      {isMobile && (
        <Fab
          variant="extended"
          color="primary"
          onClick={() => setTripDialogOpen(true)}
          sx={{
            position: 'fixed', bottom: 24, right: 16, left: 16,
            width: 'calc(100% - 32px)', borderRadius: 3,
            bgcolor: THEME_COLORS.primary.main,
            '&:hover': { bgcolor: THEME_COLORS.primary.dark || '#3d0f0f' },
            zIndex: 1100,
          }}
        >
          <TripIcon sx={{ mr: 1 }} />
          Plan This Trip
        </Fab>
      )}

      {/* Create Trip Dialog */}
      <CreateTripFromEventDialog
        open={tripDialogOpen}
        onClose={() => setTripDialogOpen(false)}
        guide={guide}
      />
    </>
  );
};

/** Reusable section wrapper */
const SectionCard = ({ icon, title, color, children, sx = {} }) => (
  <Box sx={{ mb: 4, ...sx }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 1.5,
        bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
      </Box>
      <Typography variant="h6" fontWeight={700}>{title}</Typography>
    </Box>
    {children}
  </Box>
);

export default EventGuideDetail;
